<script setup lang="ts">
import { ArrowRight } from 'lucide-vue-next'
import {computed, onMounted, type Ref, ref, watch } from "vue";
import type {DebtorContactResponse} from "../../../responses/DebtorContactResponse.ts";
import {Money} from "../../../utils/money.ts";
import {label} from '../../mixins/translations.js';
import {useCartStore} from "../../stores/cart";
import CartDiscount from "./CartDiscount.vue";
import CartEmptyMessage from "./CartEmptyMessage.vue";
import CartItem from "./CartItem.vue";
import CartPriceLoadingSkeleton from "./CartPriceLoadingSkeleton.vue";

const cartStore = useCartStore();

interface Props {
    user?: DebtorContactResponse
    route: string
    loginRoute?: string
}

const props = withDefaults<Props>(defineProps<Props>(), {})
const discountCode = ref('')

</script>
<template>
    <div class="w-full bg-white">
        <div class="mx-auto container max-w-7xl w-full px-4 py-6 lg:py-16">
            <div class="py-16" v-if="cartStore.itemCount === 0 && !cartStore.isLoadingFull" >
                <CartEmptyMessage class="max-w-2xl mx-auto"/>
            </div>
            <div class="space-y-8 lg:space-y-10" v-else>
                <h2 class="text-3xl font-bold tracking-tight text-secondary-900">
                    {{ label('site.cart.title') }}
                </h2>
                <CartItem
                    :item="item"
                    v-for="item in cartStore.items"
                    :key="item.model.id"
                >
                </CartItem>

                <section class="grid gap-8 pt-2 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
                    <div class="flex flex-col gap-10">
                        <CartDiscount></CartDiscount>

                        <div>
                            <a href="/" class="inline-flex h-12 items-center justify-center rounded-full border border-slate-200 bg-white px-8 text-base font-medium text-secondary-700 transition hover:bg-secondary-50">
                                {{ label('site.cart.continue_shopping') }}
                            </a>
                        </div>
                    </div>

                    <div class="xl:justify-self-end xl:w-full">
                        <div class="w-full rounded-3xl bg-transparent xl:max-w-sm">
                            <div class="space-y-3 text-sm text-slate-800 sm:text-base">
                                <div class="flex items-center justify-between gap-4">
                                    <span>{{ label('site.cart.subtotal_incl') }}</span>
                                    <span>
                                        <CartPriceLoadingSkeleton>{{ Money.format(cartStore.cart.sub_total_incl, cartStore.cart.currency) }}</CartPriceLoadingSkeleton>
                                    </span>
                                </div>
                                <div class="flex items-center justify-between gap-4">
                                    <span>{{ label('site.cart.subtotal_excl') }}</span>
                                    <span>
                                        <CartPriceLoadingSkeleton>{{ Money.format(cartStore.cart.sub_total_excl, cartStore.cart?.currency) }}</CartPriceLoadingSkeleton>
                                    </span>
                                </div>
                                <div class="flex items-center justify-between gap-4">
                                    <span>{{ label('site.cart.delivery_costs') }}</span>
                                    <span>
                                        <CartPriceLoadingSkeleton>{{ Money.format(cartStore.cart.delivery_cost_incl ?? 0, cartStore.cart?.currency) }}</CartPriceLoadingSkeleton>
                                    </span>
                                </div>
                                <div class="flex items-center justify-between gap-4 pt-3 text-base font-semibold text-slate-950 sm:text-lg">
                                    <span>{{ label('site.cart.total_incl')}} </span>
                                    <span>
                                        <CartPriceLoadingSkeleton>{{ Money.format(cartStore.cart.total, cartStore.cart.currency) }}</CartPriceLoadingSkeleton>
                                    </span>
                                </div>
                            </div>

                            <div class="mt-8 flex justify-start xl:justify-end">
                                <a :href="route"
                                    class="inline-flex h-14 w-full items-center justify-center gap-4 rounded-full bg-primary px-8 text-lg font-medium text-white transition hover:bg-primary-300 sm:w-auto sm:min-w-44">
                                    <span>{{ label('site.cart.continue') }}</span>
                                    <ArrowRight class="h-5 w-5" />
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>
